import { useState } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { errorToast, Modal } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";

import {
  usePatchInspectionParticipantsMutation,
  type InspectionParticipant,
  type InspectionParticipantUser,
  type InspectionStatus,
} from "../../../api/inspection";
import { signatureFontFamily } from "../inspection-signature-fonts";
import { formatInspectionDateTime } from "../../../utils/inspectionDate";
import { isDuplicateParticipant } from "../../../utils/inspectionParticipantIdentity";
import InspectionExtraParticipantFields, {
  type ExtraParticipantSource,
} from "../../../components/organisms/InspectionExtraParticipantFields";

/**
 * `manage` = aba Dados gerais (cadastro dos extras); `sign` = aba Assinaturas,
 * que só coleta assinatura e nunca altera a lista (form §7.1 / QT-INSP-08-04).
 */
export type InspectionParticipantsMode = "manage" | "sign";

export interface InspectionParticipantsStepProps {
  structureId: string;
  inspectionId: string;
  participants: InspectionParticipant[];
  canWrite: boolean;
  inspectionStatus?: InspectionStatus;
  mode?: InspectionParticipantsMode;
  /** Abre o modal de assinatura tipográfica do participante. */
  onSign?: (participant: InspectionParticipant) => void;
  /** Y=11 devolve só a lista atualizada (endpoints §12.2), não o detalhe. */
  onSaved?: (participants: InspectionParticipant[]) => void;
}

/**
 * Cadastro de participantes (form §2.1 / §7.2). Responsável é imutável; extras
 * podem ser adicionados e removidos enquanto não assinam. Não existe papel de
 * aprovador/gestor na v1. Extra = usuário do tenant (Y=15) ou só o nome.
 */
const InspectionParticipantsStep = ({
  structureId,
  inspectionId,
  participants,
  canWrite,
  inspectionStatus = "inProgress",
  mode = "manage",
  onSign,
  onSaved,
}: InspectionParticipantsStepProps) => {
  const { translate, language } = useLocalization();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [extraSource, setExtraSource] = useState<ExtraParticipantSource>("gs3User");
  const [newName, setNewName] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] =
    useState<InspectionParticipantUser | null>(null);
  const [newNameError, setNewNameError] = useState(false);

  const { mutate: patchParticipants } =
    usePatchInspectionParticipantsMutation(structureId, inspectionId);

  const editable = ["open", "inProgress"].includes(inspectionStatus);
  const canManage = mode === "manage" && canWrite && editable;
  const allSigned = participants.every((participant) => participant.hasSignature);

  const submit = (
    payload: Parameters<typeof patchParticipants>[0],
    onDone?: () => void,
  ) =>
    patchParticipants(payload, {
      onSuccess: (participants) => {
        onDone?.();
        onSaved?.(participants);
      },
    });

  const resetAddDraft = () => {
    setExtraSource("gs3User");
    setNewName("");
    setSelectedUserId("");
    setSelectedUser(null);
    setNewNameError(false);
  };

  const handleSourceChange = (source: ExtraParticipantSource) => {
    setExtraSource(source);
    setNewName("");
    setSelectedUserId("");
    setSelectedUser(null);
    setNewNameError(false);
  };

  const existingIdentities = participants.map((participant) => ({
    name: participant.name,
    participantUserId: participant.participantUserId,
  }));
  const excludedUserIds = existingIdentities
    .map((item) => item.participantUserId)
    .filter((userId): userId is string => Boolean(userId));

  const rejectIfDuplicate = (next: {
    name: string;
    participantUserId?: string | null;
  }) => {
    if (!isDuplicateParticipant(next, existingIdentities)) {
      return false;
    }
    errorToast({
      description: translate("inspection.form.extraDuplicate"),
    });
    return true;
  };

  const handleAdd = () => {
    if (extraSource === "gs3User") {
      if (!selectedUser) {
        return;
      }

      if (
        rejectIfDuplicate({
          name: selectedUser.name,
          participantUserId: selectedUser.id,
        })
      ) {
        return;
      }

      submit(
        {
          add: [
            {
              name: selectedUser.name,
              participantUserId: selectedUser.id,
            },
          ],
        },
        () => {
          resetAddDraft();
          setIsAddOpen(false);
        },
      );
      return;
    }

    const name = newName.trim();

    if (!name) {
      setNewNameError(true);
      return;
    }

    if (rejectIfDuplicate({ name })) {
      return;
    }

    setNewNameError(false);
    submit({ add: [{ name }] }, () => {
      resetAddDraft();
      setIsAddOpen(false);
    });
  };

  const roleLabel = (participant: InspectionParticipant) =>
    participant.role === "responsible"
      ? translate("inspection.participants.roleResponsible")
      : translate("inspection.participants.roleExtra");

  const extraKindLabel = (participant: InspectionParticipant) => {
    if (participant.role !== "extra") return null;
    return participant.participantUserId
      ? translate("inspection.participants.kindGs3")
      : translate("inspection.participants.kindExternal");
  };

  return (
    <div className="flex flex-col gap-3">
      {mode === "sign" ? (
        <>
          <p className="text-sm text-[var(--insp-muted,#808080)]">
            {translate("inspection.participants.signLead")}
          </p>
          {!allSigned ? (
            <p className="insp-hint">{translate("inspection.participants.finalizeHint")}</p>
          ) : null}
        </>
      ) : (
        <div className="insp-part-block">
          <div className="insp-pb-head">
            <h4>{translate("inspection.participants.title")}</h4>
            {canManage ? (
              <button
                type="button"
                className="insp-btn insp-btn-primary insp-btn-sm"
                onClick={() => setIsAddOpen(true)}
              >
                <Plus aria-hidden size={14} />
                {translate("inspection.participants.add")}
              </button>
            ) : null}
          </div>

          <ul className="insp-part-list">
            {participants.map((participant) => {
              const isExtra = participant.role === "extra";
              const canRemove = canManage && isExtra && !participant.hasSignature;
              const extraKind = extraKindLabel(participant);

              return (
                <li key={participant.id} data-testid="participant-row">
                  <div className="insp-part-who">
                    <span>
                      <span>{participant.name}</span>
                      {" · "}
                      <small>{roleLabel(participant)}</small>
                      {extraKind ? (
                        <span className="insp-part-kind">{extraKind}</span>
                      ) : null}
                    </span>
                    <small>
                      {participant.hasSignature
                        ? translate("inspection.participants.signed")
                        : translate("inspection.participants.pending")}
                    </small>
                  </div>

                  {canRemove ? (
                    <button
                      type="button"
                      className="insp-grip-btn"
                      aria-label={translate("inspection.participants.remove")}
                      onClick={() => submit({ remove: [participant.id] })}
                    >
                      <Trash2 aria-hidden size={16} />
                    </button>
                  ) : participant.hasSignature ? (
                    <span className="insp-pill-signed">
                      <Check aria-hidden size={14} />
                      {translate("inspection.participants.signed")}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {mode === "sign" ? (
        <ul className="flex flex-col">
          {participants.map((participant) => {
            const canCollectSignature =
              Boolean(onSign) && canWrite && inspectionStatus === "inProgress";

            return (
              <li
                key={participant.id}
                data-testid="participant-row"
                className={`insp-sign-card${participant.hasSignature ? " is-signed" : ""}`}
              >
                <div className="insp-sign-who">
                  <strong>
                    <span>{participant.name}</span>
                    {" · "}
                    {roleLabel(participant)}
                  </strong>
                  {participant.hasSignature ? (
                    <>
                      <small>
                        {translate("inspection.signature.signedAt")}{" "}
                        {formatInspectionDateTime(participant.signedAt, language)}
                      </small>
                      <div
                        className="insp-sig-render"
                        style={{
                          fontFamily: signatureFontFamily(
                            participant.signatureFontKey,
                          ),
                        }}
                      >
                        {participant.name}
                      </div>
                    </>
                  ) : (
                    <small>
                      {translate("inspection.participants.pendingSignature")}
                    </small>
                  )}
                </div>

                {canCollectSignature ? (
                  <button
                    type="button"
                    className={`insp-btn ${
                      participant.hasSignature
                        ? "insp-btn-outline"
                        : "insp-btn-primary"
                    }`}
                    onClick={() => onSign?.(participant)}
                  >
                    {translate(
                      participant.hasSignature
                        ? "inspection.participants.resign"
                        : "inspection.participants.sign",
                    )}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      <Modal
        open={isAddOpen}
        onClose={() => {
          resetAddDraft();
          setIsAddOpen(false);
        }}
        title={
          translate("inspection.participants.addTitle")
        }
        content={
          <div className="insp-nested-shell">
            <div className="insp-nested-body flex flex-col gap-3">
            <InspectionExtraParticipantFields
              structureId={structureId}
              groupName="add-extra-source"
              source={extraSource}
              onSourceChange={handleSourceChange}
              name={newName}
              onNameChange={(value) => {
                setNewName(value);
                setNewNameError(false);
              }}
              nameError={newNameError}
              nameId="participant-new-name"
              nameLabel={translate("inspection.participants.name")}
              selectedUserId={selectedUserId}
              onUserChange={(userId, user) => {
                setSelectedUserId(userId);
                setSelectedUser(user);
              }}
              onNameSubmit={handleAdd}
              excludedUserIds={excludedUserIds}
            />
            </div>

            <div className="insp-modal-foot">
              <button
                type="button"
                className="insp-btn insp-btn-outline"
                onClick={() => setIsAddOpen(false)}
              >
                {translate("inspection.actions.cancel")}
              </button>
              <button
                type="button"
                className="insp-btn insp-btn-primary"
                onClick={handleAdd}
              >
                {translate("inspection.actions.add")}
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default InspectionParticipantsStep;
